import React, { useState, useRef, useEffect } from 'react';
import { Link } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Navigation = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navHeight, setNavHeight] = useState(0);
  const navRef = useRef(null);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  // Function to update navigation height
  const updateNavHeight = () => {
    if (navRef.current) {
      const height = navRef.current.offsetHeight;
      setNavHeight(height);
      // Apply the height to body margin or padding to push content
      document.body.style.paddingTop = `${height}px`;
    }
  };

  useEffect(() => {
    // Initial height calculation
    updateNavHeight();

    // Update height on window resize
    const handleResize = () => {
      updateNavHeight();
    };

    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      // Reset body padding when component unmounts
      document.body.style.paddingTop = '0px';
    };
  }, []);

  // Update height when mobile menu state changes
  useEffect(() => {
    // Use setTimeout to ensure DOM has updated after animation
    const timer = setTimeout(updateNavHeight, 350); // Slightly longer than animation duration
    return () => clearTimeout(timer);
  }, [mobileMenuOpen]);

  return (
    <nav
      ref={navRef}
      className="fixed top-0 z-50"
      style={{
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        left: 0,
      }}
    >
      {/* Top bar */}
      <div className="w-full">
        <div className="max-w-[1200px] mx-auto pt-2 flex justify-end lg:px-0 px-8">
          <LanguageSwitcher />
        </div>
      </div>

      {/* Main nav */}
      <div className="w-full shadow">
        <div className="max-w-[1200px] mx-auto py-4 lg:px-0 px-8 flex justify-between items-center">
          <span className="text-3xl font-bold falafel-font"><Link href="/" style={{color: 'inherit', textDecoration: 'none'}}>Felfela Kraków</Link></span>

          {/* Desktop menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link href="/" underline="none">{t('navigation.home')}</Link>
            <Link href="/menu" underline="none">{t('navigation.menu')}</Link>
            <Link href="/About/" underline="none">{t('navigation.about')}</Link>
            <Link href="/contact" underline="none">{t('navigation.contact')}</Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              className="flex flex-col items-center justify-center w-8 h-8"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <motion.span
                className="w-6 h-0.5 bg-gray-600 block mb-1.5"
                animate={mobileMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.3 }}
              />
              <motion.span
                className="w-6 h-0.5 bg-gray-600 block mb-1.5"
                animate={mobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                transition={{ duration: 0.1 }}
              />
              <motion.span
                className="w-6 h-0.5 bg-gray-600 block"
                animate={mobileMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.3 }}
              />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              className="md:hidden w-full bg-white shadow-md"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col py-4 space-y-4 lg:px-0 px-8">
                <Link href="/" underline="none" onClick={() => setMobileMenuOpen(false)}>
                  {t('navigation.home')}
                </Link>
                <Link href="/menu" underline="none" onClick={() => setMobileMenuOpen(false)}>
                  {t('navigation.menu')}
                </Link>
                <Link href="/About/" underline="none" onClick={() => setMobileMenuOpen(false)}>
                  {t('navigation.about')}
                </Link>
                <Link href="/contact" underline="none" onClick={() => setMobileMenuOpen(false)}>
                  {t('navigation.contact')}
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navigation;